import { useCallback, useEffect, useRef, useState } from 'react';
import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaRegCircle, FaRegPlayCircle } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/store';
import { AdminRole } from '../../types/constants.types';
import { Button } from '../Button';
import { getMyOnboarding } from '../../services/KioskRegistrationService';
import type { OwnerOnboarding } from '../../types/kioskRegistration.types';
import { OWNER_ONBOARDING_REFRESH_EVENT } from '../../lib/ownerOnboardingEvents';
import { OWNER_SETUP_COMPLETED_EVENT, OWNER_SETUP_OPEN_EVENT } from '../OwnerSetup/ownerSetupEvents';
import { OWNER_TOUR_START_EVENT } from '../OwnerTour/tourSteps';
import ProgressRing from './ProgressRing';

const OPEN_KEY = 'ownerGettingStartedOpen';
const SETUP_STEP = 'KIOSK_SETUP';
const CELEBRATION_MS = 6000;
const FOCUS_REFRESH_COOLDOWN_MS = 4000;
const ACTION_REFRESH_DELAY_MS = 350;

/** Expanded the very first time so new owners notice it; afterwards it remembers the owner's choice. */
function readInitialOpen(): boolean {
  try {
    const stored = localStorage.getItem(OPEN_KEY);
    if (stored === null) {
      localStorage.setItem(OPEN_KEY, '0');
      return true;
    }
    return stored === '1';
  } catch {
    return false;
  }
}

function rememberOpen(open: boolean): void {
  try {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // The choice then only lasts until the next page load.
  }
}

function PanelInner() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [onboarding, setOnboarding] = useState<OwnerOnboarding | null>(null);
  const [open, setOpen] = useState(readInitialOpen);
  const [celebrating, setCelebrating] = useState(false);
  const [gone, setGone] = useState(false);
  const hadPendingSteps = useRef(false);
  const lastFetch = useRef(0);
  const panelRef = useRef<HTMLElement>(null);

  const refresh = useCallback(async (throttle = false) => {
    const now = Date.now();
    if (throttle && now - lastFetch.current < FOCUS_REFRESH_COOLDOWN_MS) return;
    lastFetch.current = now;
    try {
      const result = await getMyOnboarding();
      if (!Array.isArray(result.steps) || result.totalCount <= 0) return;
      setOnboarding(result);
    } catch {
      // A failed refresh keeps showing the last known progress; the checklist must never get in the way.
    }
  }, []);

  // Pick up progress straight away: on every page change, after any save or action the owner takes on
  // any page, and after setup. Returning to the tab is the only throttled trigger.
  useEffect(() => {
    void refresh();
  }, [pathname, refresh]);

  useEffect(() => {
    let timer: number | undefined;
    // Bursts of requests (a save followed by a reload of the list, say) collapse into one refresh.
    const onAction = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void refresh(), ACTION_REFRESH_DELAY_MS);
    };
    const onFocus = () => void refresh(true);
    const onSetupDone = () => void refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener(OWNER_SETUP_COMPLETED_EVENT, onSetupDone);
    window.addEventListener(OWNER_ONBOARDING_REFRESH_EVENT, onAction);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(OWNER_SETUP_COMPLETED_EVENT, onSetupDone);
      window.removeEventListener(OWNER_ONBOARDING_REFRESH_EVENT, onAction);
    };
  }, [refresh]);

  const complete = Boolean(onboarding && onboarding.completedCount >= onboarding.totalCount);

  // Once everything is done, say so briefly and then remove the panel for good. Keyed on `complete` and
  // `loaded` only, so a background refresh while the message is showing cannot restart the countdown.
  const loaded = onboarding !== null;
  useEffect(() => {
    if (!loaded) return;
    if (!complete) {
      hadPendingSteps.current = true;
      return;
    }
    if (!hadPendingSteps.current) {
      setGone(true); // Already complete when loaded: nothing to show.
      return;
    }
    setCelebrating(true);
    setOpen(true);
    const timer = window.setTimeout(() => setGone(true), CELEBRATION_MS);
    return () => window.clearTimeout(timer);
  }, [complete, loaded]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && panelRef.current?.contains(document.activeElement)) {
        setOpen(false);
        rememberOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!onboarding || gone) return null;

  const toggle = (next: boolean) => {
    setOpen(next);
    rememberOpen(next);
  };

  const nextStep = onboarding.steps.find((step) => !step.completed);
  const percent = Math.round((onboarding.completedCount / onboarding.totalCount) * 100);

  const runStep = (step: OwnerOnboarding['steps'][number]) => {
    if (step.key === SETUP_STEP) {
      window.dispatchEvent(new CustomEvent(OWNER_SETUP_OPEN_EVENT));
    } else if (step.path) {
      // The share actions (link, TV link, QR) live on Kiosks; older API responses pointed at the page the owner is already on.
      navigate(step.key === 'SHARE_CHANNEL' ? '/kiosks' : step.path);
    }
  };

  return (
    <aside
      ref={panelRef}
      data-tour="getting-started"
      aria-label="Getting started"
      className="pointer-events-none fixed right-0 top-24 z-40 flex max-w-full justify-end"
    >
      {/* Collapsed tab */}
      <button
        type="button"
        onClick={() => toggle(true)}
        aria-expanded={false}
        aria-controls="getting-started-panel"
        tabIndex={open ? -1 : 0}
        className={`pointer-events-auto flex items-center gap-3 rounded-l-2xl border border-r-0 border-emerald-200 bg-white/95 py-3 pl-3 pr-2 text-left shadow-lg backdrop-blur motion-safe:transition-all motion-safe:duration-300 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-slate-900/95 dark:hover:bg-slate-800 ${
          open ? 'pointer-events-none absolute right-0 translate-x-full opacity-0' : 'translate-x-0 opacity-100'
        }`}
      >
        <ProgressRing completed={onboarding.completedCount} total={onboarding.totalCount} />
        <span className="hidden min-w-0 sm:block">
          <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Getting started</span>
          <span className="block max-w-[160px] truncate text-xs text-gray-500 dark:text-gray-400">
            {nextStep ? `Next: ${nextStep.title}` : 'All done'}
          </span>
        </span>
        <FaChevronLeft className="text-gray-400" aria-hidden="true" />
      </button>

      {/* Expanded card */}
      <section
        id="getting-started-panel"
        aria-hidden={!open}
        className={`pointer-events-auto w-[360px] max-w-[calc(100vw-0.5rem)] overflow-hidden rounded-l-2xl border border-r-0 border-emerald-200 bg-white shadow-2xl motion-safe:transition-all motion-safe:duration-300 dark:border-emerald-900/60 dark:bg-slate-900 ${
          open ? 'translate-x-0 opacity-100' : 'invisible absolute right-0 translate-x-full opacity-0'
        }`}
      >
        <header className="flex items-center gap-3 border-b border-emerald-100 bg-emerald-50/70 px-4 py-3 dark:border-emerald-900/50 dark:bg-emerald-950/30">
          <ProgressRing completed={onboarding.completedCount} total={onboarding.totalCount} size={48} />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Getting started</h2>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {celebrating ? 'Everything is set up' : `${percent}% complete`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggle(false)}
            aria-label="Collapse getting started"
            aria-expanded={open}
            aria-controls="getting-started-panel"
            className="rounded-lg p-2 text-gray-500 hover:bg-white/80 dark:text-gray-300 dark:hover:bg-slate-800"
          >
            <FaChevronRight aria-hidden="true" />
          </button>
        </header>

        {celebrating ? (
          <div role="status" className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <FaCheckCircle className="text-4xl text-emerald-600" aria-hidden="true" />
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">You're all set!</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Your channel is fully set up and ready to share. This checklist will now go away.
            </p>
          </div>
        ) : (
          <>
            <ul className="max-h-[calc(100vh-17rem)] min-h-[160px] space-y-1 overflow-y-auto px-3 py-3">
              {onboarding.steps.map((step) => {
                const isNext = !step.completed && step.key === nextStep?.key;
                const actionable = !step.completed && (step.key === SETUP_STEP || Boolean(step.path));
                return (
                  <li
                    key={step.key}
                    className={`flex items-start gap-3 rounded-xl px-3 py-2.5 ${
                      isNext ? 'border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30' : ''
                    }`}
                  >
                    {step.completed ? (
                      <FaCheckCircle className="mt-0.5 shrink-0 text-emerald-600" aria-label="Completed" />
                    ) : (
                      <FaRegCircle className="mt-0.5 shrink-0 text-gray-400" aria-label="Not completed" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm font-medium ${
                          step.completed ? 'text-gray-500 line-through dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {step.title}
                      </p>
                      {!step.completed && <p className="text-xs text-gray-600 dark:text-gray-300">{step.description}</p>}
                      {actionable && (
                        <div className="mt-2">
                          <Button size="sm" variant={isNext ? 'primary' : 'secondary'} onClick={() => runStep(step)}>
                            {step.key === SETUP_STEP ? 'Complete setup' : isNext ? 'Set up now' : 'Open'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <footer className="border-t border-gray-100 px-4 py-3 dark:border-white/10">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent(OWNER_TOUR_START_EVENT))}
                className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400"
              >
                <FaRegPlayCircle aria-hidden="true" />
                Take the product tour
              </button>
            </footer>
          </>
        )}
      </section>
    </aside>
  );
}

/** Mounted for every signed-in user; only channel owners see it, and it removes itself once everything is done. */
export default function OwnerGettingStartedPanel() {
  const role = useSelector((state: RootState) => state.auth.role);
  if (role !== AdminRole.KIOSK_OWNER) return null;
  return <PanelInner />;
}
